import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

import { Typography, Button } from "@mui/material";
import { ChevronDown, ChevronUp } from "lucide-react";

import { decimalToFraction, formatAmountUnit, formatIngredient, formatState } from "../../../utils/recipe";

import CustomModal from "../../modal/CustomModal";

const IngredientItem = ({ ingredient }) => {
  return (
    <li className="my-3 marker:text-mainGreen">
      <div className="flex flex-col">
        <Typography variant="body1" className="font-medium">
          <span className="font-normal">{formatAmountUnit(ingredient)}</span>
          {formatIngredient(ingredient)}
          {ingredient.state !== "" && (
            <span className="font-normal italic text-secondaryText text-sm">{formatState(ingredient)}</span>
          )}
        </Typography>
        {ingredient.note && ingredient.note !== "" && (
          <Typography variant="body2" className="text-secondaryText">
            ({ingredient.note})
          </Typography>
        )}
      </div>
    </li>
  );
};

const YieldDisplay = ({ yield: yieldObj }) => {
  return (
    <div className="flex flex-row justify-between items-center py-2">
      <Typography variant="subtitle2" className="font-normal leading-snug text-secondaryText">
        Yield:{" "}
        <span className="font-medium">
          {formatAmountUnit(yieldObj)}{" "}
          {yieldObj.ingredient && <span>{formatIngredient(yieldObj.ingredient.name)}</span>}
        </span>
      </Typography>
    </div>
  );
};

const HistoryItemPantrySection = ({ section }) => {
  const [showUsed, setShowUsed] = useState(true);
  const [showAdded, setShowAdded] = useState(true);

  return (
    <div key={`pantry-section-${section.recipeSectionIndex}`} className="pb-2">
      {section?.name &&
        section.name !== "" &&
        (section?.ingredientsUsed?.length > 0 || section?.ingredientsAdded?.length > 0) && (
          <div className="p-4 bg-neutral-100">
            <Typography variant="h6" className="font-normal">
              {section.name}
            </Typography>
          </div>
        )}

      {section.yield && <YieldDisplay yield={section.yield} />}

      {section.ingredientsUsed?.length > 0 && (
        <div className="mt-4 px-4 md:rounded-b-md">
          <div className="flex items-center gap-2 cursor-pointer mb-2" onClick={() => setShowUsed(!showUsed)}>
            <motion.div initial={false} animate={{ rotate: showUsed ? 180 : 0 }} transition={{ duration: 0.2 }}>
              {showUsed ? (
                <ChevronUp className="w-4 h-4 text-secondaryText-600" />
              ) : (
                <ChevronDown className="w-4 h-4 text-secondaryText-600" />
              )}
            </motion.div>
            <Typography variant="subtitle2" className="text-primaryText font-normal">
              Ingredients Used
              {section?.ingredientsUsed?.length && section.ingredientsUsed.length > 1 && (
                <span className="text-mainGreen font-medium ml-1 text-sm leading-snug">
                  {section.ingredientsUsed.length}
                </span>
              )}
            </Typography>
          </div>
          <AnimatePresence initial={false}>
            {showUsed && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.2, ease: "easeInOut" }}
                className="overflow-hidden"
              >
                <ul className="list-disc list-outside ml-4">
                  {section.ingredientsUsed
                    .filter(
                      (ingredient) => ingredient.unit?.display?.magnitude || ingredient.unit?.display?.text === "self"
                    )
                    .map((ingredient, j) => (
                      <IngredientItem key={`used-${j}`} ingredient={ingredient} />
                    ))}
                </ul>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}

      {section.ingredientsAdded?.length > 0 && (
        <div className="mt-4 px-4 md:rounded-b-md">
          <div className="flex items-center gap-2 cursor-pointer mb-2" onClick={() => setShowAdded(!showAdded)}>
            <motion.div initial={false} animate={{ rotate: showAdded ? 180 : 0 }} transition={{ duration: 0.2 }}>
              {showAdded ? (
                <ChevronUp className="w-4 h-4 text-secondaryText-600" />
              ) : (
                <ChevronDown className="w-4 h-4 text-secondaryText-600" />
              )}
            </motion.div>
            <Typography variant="subtitle2" className="text-primaryText font-normal">
              Ingredients Added
              {section?.ingredientsAdded?.length && section.ingredientsAdded.length > 1 && (
                <span className="text-mainGreen font-medium ml-1 text-sm leading-snug">
                  {section.ingredientsAdded.length}
                </span>
              )}
            </Typography>
          </div>
          <AnimatePresence initial={false}>
            {showAdded && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.2, ease: "easeInOut" }}
                className="overflow-hidden"
              >
                <ul className="list-disc list-outside ml-4">
                  {section.ingredientsAdded.map((ingredient, j) => (
                    <IngredientItem key={`added-${j}`} ingredient={ingredient} />
                  ))}
                </ul>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
};

export default HistoryItemPantrySection;
